/**
 * Onglet « Intervenants » d'une simulation DOS-DOMIUM.
 * CRUD progressif (raison sociale, SIRET, lot/mission, montant TTC, ref couverture).
 * Chaque mutation déclenche côté backend la régénération du Dossier Maître et
 * des Attestations DRAFT/Définitive, qui exploitent automatiquement la liste.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Building2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSiretAutoComplete, SiretInput } from '../useSiretAutoComplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntervenantsManagerDialog({ open, onOpenChange, simulation }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const initialDraft = {
    raison_sociale: '',
    siret: '',
    mission: '',
    ville: '',
    montant_ttc: '',
    reference_couverture: '',
  };
  const [draft, setDraft] = useState(initialDraft);
  const {
    siret: siretFieldValue,
    setSiret: setSiretField,
    lookup: siretLookup,
  } = useSiretAutoComplete({
    initialSiret: '',
    onAutoFill: (company) => {
      setDraft((p) => ({
        ...p,
        raison_sociale: p.raison_sociale.trim() || (company.raison_sociale || ''),
        ville: p.ville.trim() || (company.ville || ''),
      }));
    },
  });
  useEffect(() => {
    setDraft((p) => ({ ...p, siret: siretFieldValue }));
  }, [siretFieldValue]);

  const ref = simulation?.reference;

  const reload = React.useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(ref)}/intervenants`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(r.data?.intervenants || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger les intervenants');
    } finally {
      setLoading(false);
    }
  }, [ref]);

  React.useEffect(() => {
    if (open) {
      setDraft(initialDraft);
      setEditingId(null);
      reload();
    }
  }, [open, ref]);

  const setField = (k) => (e) => setDraft((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!ref) return;
    if (!draft.raison_sociale.trim()) {
      toast.error('La raison sociale est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        raison_sociale: draft.raison_sociale.trim(),
        siret: draft.siret.trim() || null,
        mission: draft.mission.trim() || null,
        ville: draft.ville.trim() || null,
        montant_ttc: draft.montant_ttc === '' ? null : Number(draft.montant_ttc),
        reference_couverture: draft.reference_couverture.trim() || null,
      };
      const url = editingId
        ? `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(ref)}/intervenants/${editingId}`
        : `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(ref)}/intervenants`;
      const method = editingId ? 'put' : 'post';
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        editingId
          ? 'Intervenant mis à jour — PDFs régénérés en arrière-plan'
          : 'Intervenant ajouté — PDFs régénérés en arrière-plan'
      );
      setDraft(initialDraft);
      setEditingId(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (it) => {
    setEditingId(it.id);
    setDraft({
      raison_sociale: it.raison_sociale || '',
      siret: it.siret || '',
      mission: it.mission || '',
      ville: it.ville || '',
      montant_ttc: it.montant_ttc != null ? String(it.montant_ttc) : '',
      reference_couverture: it.reference_couverture || '',
    });
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Supprimer l'intervenant « ${it.raison_sociale} » ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(ref)}/intervenants/${it.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Intervenant supprimé — PDFs régénérés en arrière-plan');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n));
    } catch {
      return String(n);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto border-slate-200 shadow-xl" data-testid="intervenants-dialog">
        <DialogHeader className="border-b border-slate-100 pb-4">
          <DialogTitle className="flex items-center gap-2.5 text-slate-900">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-purple-50 text-purple-600">
              <Building2 className="h-4 w-4" />
            </span>
            <div className="flex flex-col">
              <span className="font-semibold tracking-tight">Intervenants à l&apos;opération</span>
              <span className="text-xs font-mono text-slate-500 mt-0.5">{simulation?.reference}</span>
            </div>
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm leading-relaxed mt-1.5">
            Saisie progressive des entreprises et bureaux d&apos;études prenant part à l&apos;opération.
            Chaque ajout / modification / suppression déclenche automatiquement la
            régénération du Dossier Maître et des Attestations.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4">
          <h3 className="text-sm font-bold text-gray-900 mb-2">
            Intervenants enregistrés {items.length > 0 && <span className="text-gray-500">({items.length})</span>}
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500" data-testid="intervenants-empty">
              Aucun intervenant déclaré à ce stade.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Raison sociale</th>
                    <th className="px-3 py-2 text-left font-semibold">SIRET</th>
                    <th className="px-3 py-2 text-left font-semibold">Mission / lot</th>
                    <th className="px-3 py-2 text-right font-semibold">Montant TTC</th>
                    <th className="px-3 py-2 text-left font-semibold">Réf. couverture</th>
                    <th className="px-3 py-2 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`intervenant-row-${it.id}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {it.raison_sociale}
                        {it.ville && <div className="text-xs text-gray-500">{it.ville}</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.siret || '—'}</td>
                      <td className="px-3 py-2">{it.mission || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatMoney(it.montant_ttc)}</td>
                      <td className="px-3 py-2 text-xs">{it.reference_couverture || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => handleEdit(it)} className="text-indigo-600 hover:text-indigo-800 mr-2" title="Modifier" data-testid={`btn-edit-intervenant-${it.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(it)} className="text-red-600 hover:text-red-800" title="Supprimer" data-testid={`btn-delete-intervenant-${it.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3">
            {editingId ? "Modifier l'intervenant" : 'Ajouter un nouvel intervenant'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="iv_raison">Raison sociale *</Label>
              <Input id="iv_raison" placeholder="Ex : BÂTI ATLANTIQUE SARL" value={draft.raison_sociale} onChange={setField('raison_sociale')} data-testid="input-iv-raison" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iv_siret">SIRET</Label>
              <SiretInput id="iv_siret" value={siretFieldValue} onChange={setSiretField} lookup={siretLookup} testId="input-iv-siret" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iv_ville">Ville</Label>
              <Input id="iv_ville" placeholder="Ex : Pointe-à-Pitre" value={draft.ville} onChange={setField('ville')} data-testid="input-iv-ville" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iv_mission">Mission / lot</Label>
              <Input id="iv_mission" placeholder="Ex : Lot Gros-Œuvre" value={draft.mission} onChange={setField('mission')} data-testid="input-iv-mission" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iv_montant">Montant devis TTC (€)</Label>
              <Input id="iv_montant" type="number" step="0.01" min="0" placeholder="Ex : 85000" value={draft.montant_ttc} onChange={setField('montant_ttc')} data-testid="input-iv-montant" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="iv_couverture">Référence de couverture</Label>
              <Input id="iv_couverture" placeholder="Ex : DOMIUM-2026-00012" value={draft.reference_couverture} onChange={setField('reference_couverture')} data-testid="input-iv-couverture" />
            </div>
          </div>
        </div>

        <DialogFooter>
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setDraft(initialDraft); }} disabled={submitting} data-testid="btn-cancel-edit-intervenant">
              Annuler l&apos;édition
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="btn-close-intervenants">
            Fermer
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !draft.raison_sociale.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-save-intervenant">
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement…</>
            ) : editingId ? (
              <><Save className="h-4 w-4 mr-2" />Mettre à jour</>
            ) : (
              <><Building2 className="h-4 w-4 mr-2" />Ajouter</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
